import { Box, Button, CircularProgress } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { datasourceModalOpenState } from '~/state/atoms/viewerAtoms';

import { Download } from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import { PatientDTO } from '~/backend/connector-api/connector-schemas';
import { downloadDicomSeries } from '~/backend/connector-api/utils';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import {
    SeriesData,
    SeriesDataObject,
    transformAvailablePatientData,
} from '~/utils/transforms/patientDataTransforms';
import PatientAccordion from '../PatientAccordion';
import useActiveItems from '../useActiveItems';
import PatientDataTable from './DatasetTable';

export type SelectedDatasets = {
    [modality: string]: { [seriesId: string]: SeriesDataObject };
};

export default function AvailablePatientData({
    patient,
}: {
    patient: PatientDTO;
}) {
    const { initializeEnvironment } = useViewportEnvironment();
    const { processImportedFiles } = useProcessDicomFiles();

    const [exporting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedDatasets, setSelectedDatasets] = useState<SelectedDatasets>(
        {}
    );

    const setDatasourceModal = useSetRecoilState(datasourceModalOpenState);
    const activeItems = useActiveItems();

    const data = transformAvailablePatientData(patient);

    const handleCheckSelect = (
        _: React.MouseEvent<HTMLElement, MouseEvent>,
        series: SeriesData
    ) => {
        const modalityData = selectedDatasets[series.modality] || {};
        const seriesIds = Object.keys(modalityData);
        if (seriesIds.some((value) => value === series.id)) {
            setSelectedDatasets((selected) => {
                // Clone the relevant modality object to avoid directly mutating state
                const updatedModalityData = { ...modalityData };
                // Use delete to remove the series from the cloned object
                delete updatedModalityData[series.id];
                return {
                    ...selected,
                    [series.modality]: updatedModalityData,
                };
            });
        } else {
            setSelectedDatasets((selected) => ({
                ...selected,
                [series.modality]: {
                    ...modalityData,
                    [series.id]: series.seriesDataObject,
                },
            }));
        }
    };

    const handleLoadDatasets = async () => {
        try {
            setLoading(true);
            const allFiles: File[] = [];

            // Download all selected series
            for (const modality of Object.keys(selectedDatasets)) {
                const modalityData = selectedDatasets[modality];
                for (const seriesId of Object.keys(modalityData)) {
                    const series = data?.series?.find((s) => s.id === seriesId);
                    if (series?.study) {
                        const blob = await downloadDicomSeries(
                            series.study.studyInstanceUID,
                            series.id
                        );
                        if (blob) {
                            const file = new File([blob], `${series.id}.zip`);
                            allFiles.push(file);
                        }
                    }
                }
            }

            if (allFiles.length > 0) {
                const importedItems = await processImportedFiles(allFiles);
                if (importedItems) {
                    initializeEnvironment(importedItems);
                    setDatasourceModal(false);
                } else {
                    enqueueSnackbar('There was no files to import.', {
                        variant: 'warning',
                    });
                }
            }
        } catch (error) {
            enqueueSnackbar('Failed to load datasets from server.', {
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadDatasets = async () => {
        console.warn(
            'TODO: Logic to download files from DICOM Server not implemented yet'
        );
    };

    const datasetSelected = Object.keys(selectedDatasets).length > 0;

    return (
        <PatientAccordion patientKey={patient.patientId} patientData={data}>
            {data?.series ? (
                <PatientDataTable
                    rows={data.series}
                    selectedDatasets={selectedDatasets}
                    onCheckSelect={handleCheckSelect}
                    activeItems={activeItems}
                />
            ) : (
                <Typography>No datasets for this patient.</Typography>
            )}
            <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
                <Button
                    onClick={handleDownloadDatasets}
                    variant="text"
                    color="primary"
                    size="small"
                    disabled={!datasetSelected || exporting}
                    startIcon={
                        exporting ? (
                            <CircularProgress size={12} />
                        ) : (
                            <Download size={12} />
                        )
                    }
                >
                    <FormattedMessage id="downloadDatasets" />
                </Button>
                <Button
                    onClick={handleLoadDatasets}
                    variant="outlined"
                    color="primary"
                    size="small"
                    disabled={!datasetSelected || exporting || loading}
                    startIcon={
                        loading ? <CircularProgress size={12} /> : undefined
                    }
                >
                    <FormattedMessage id="load" />
                </Button>
            </Box>
        </PatientAccordion>
    );
}
